import { EntriesClient } from "~/api/entries";
import { stripParagraphPropertiesForLocalStorage } from "~/utils/stripParagraphPropertiesForLocalStorage";
import type {
  IDetailParagraphEntry,
  ILinkChain,
  INewUserEntry,
  IParagraphEntry,
} from "~/models/IUserEntry";
import type { IPosition } from "~/models/IPosition";
import type { ILegalTextParagraph } from "~/models/ILegalTextParagraph";

export interface LocalSyncPayload<
  T,
  Data extends IDetailParagraphEntry = IDetailParagraphEntry,
> {
  payload: {
    paragraphId: number;
    entry: T;
  };
  data: Data;
}

export function useEntries() {
  const { user } = storeToRefs(useUserStore());
  const { addEntry, updateEntry: updateLocalEntry } = useLocalEntriesSync();
  const { data: legalText } = useCurrentLegalText();
  const {
    query: { data: legalTextParagraph },
  } = useCurrentParagraph();
  const { setActive } = useSetActiveEntry();
  const { createEntryMutation, updateEntryMutation } = entriesMutations();
  const api = useApiClient(EntriesClient);

  async function moveEntry<T extends IParagraphEntry>(
    entry: T,
    position: IPosition
  ) {
    if (user.value) {
      await updateEntry({ ...entry, position });
    } else {
      const updatedEntry = {
        ...entry,
        position,
      };
      updateLocalEntry({
        payload: {
          paragraphId: legalTextParagraph.value!.id,
          entry: updatedEntry,
        },
        data: {
          ...updatedEntry,
          legal_text_paragraph: stripParagraphPropertiesForLocalStorage(
            legalTextParagraph.value!,
            legalText.value!
          ),
        } as IDetailParagraphEntry,
      });
    }
  }

  async function updateEntry<T extends IParagraphEntry>(entry: T) {
    if (user.value) {
      const returnedEntry = await updateEntryMutation.mutateAsync(entry);
      if (returnedEntry.type === "schema") {
        await setActive(returnedEntry);
      }
    } else {
      updateLocalEntry({
        payload: {
          paragraphId: entry.legal_text_paragraph_id,
          entry,
        },
        data: {
          ...entry,
        } as IDetailParagraphEntry,
      });
    }
  }

  async function saveEntry<T extends INewUserEntry>(
    targetLegalTextParagraph: ILegalTextParagraph,
    entry: T
  ) {
    if (entry.type === "note") {
      entry.color = "yellow";
    }

    if (entry.type === "link_chain") {
      (entry as ILinkChain).links = (entry as ILinkChain).links.map((link) => ({
        ...link,
        editable: true,
      }));
    }

    return syncEntry({
      payload: {
        paragraphId: targetLegalTextParagraph.id,
        entry,
      },
      data: {
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        is_owned: true,
        id: Date.now(),
        legal_text_paragraph_id: targetLegalTextParagraph.id,
        title: targetLegalTextParagraph.title ?? "Title",
        legal_text_paragraph: stripParagraphPropertiesForLocalStorage(
          targetLegalTextParagraph,
          targetLegalTextParagraph.legal_text!
        ),
        editable: true,
        is_shared: false,
        ...entry,
      } as IDetailParagraphEntry,
    });
  }

  async function syncEntry<T extends LocalSyncPayload<INewUserEntry>>(
    data: T
  ): Promise<IDetailParagraphEntry> {
    const { paragraphId, entry } = data.payload;
    if (user.value) {
      return await createEntryMutation.mutateAsync({ paragraphId, entry });
    } else {
      const { title } = await api.getEntryTitle({
        wordStartOffset: entry.position.word_start_offset,
        nodeStartId: entry.position.node_start_id,
      });

      const localEntry = {
        ...data.data,
        title,
      };

      addEntry({
        ...data,
        data: localEntry,
      });

      return localEntry;
    }
  }

  return {
    updateEntry,
    saveEntry,
    moveEntry,
  };
}
